import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, Truck, MapPin, Percent, ChevronDown, Layers } from "lucide-react";
import api from "@/lib/api";
import { ProductCard, GRADE_ORDER } from "@/components/ProductCard";
import { CONDITION_SHORT } from "@/components/ConditionBadge";
import { Seo } from "@/components/Seo";
import { LiveActivityTicker } from "@/components/LiveActivityTicker";
import { BestSellersRow } from "@/components/BestSellersRow";

const heroImg = "https://images.pexels.com/photos/38195854/pexels-photo-38195854.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function groupBySku(products) {
  const map = new Map();
  for (const p of products) {
    const sku = (p.sku || "").trim();
    const key = sku ? `sku:${sku}` : `id:${p.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }
  return [...map.entries()].map(([key, variants]) => {
    const sorted = variants.slice().sort((a, b) => GRADE_ORDER.indexOf(a.condition) - GRADE_ORDER.indexOf(b.condition));
    return { key, primary: sorted[0], variants: sorted };
  });
}

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ conditions: [], categories: [], lots: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [conditions, setConditions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [inStock, setInStock] = useState(false);
  const [sort, setSort] = useState("newest");

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (conditions.length) params.condition = conditions.join(",");
    if (categories.length) params.category = categories.join(",");
    if (inStock) params.in_stock = true;
    if (sort) params.sort = sort;
    try {
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } finally { setLoading(false); }
  }, [search, conditions, categories, inStock, sort]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const skuGroups = groupBySku(products);

  return (
    <div>
      <Seo
        title="Liquidation & Auction-Lot Deals for Short-Term Rental Owners"
        description="Shop surplus, liquidation and auction-lot goods for Airbnb & vacation rentals. Below-list, condition-graded small appliances, coffee makers, linens & electronics for STR owners, operators and managers. Fort Lauderdale, FL — ships UPS Ground nationwide."
        path="/"
      />
      {/* HERO */}
      <section className="relative border-b-2 border-ink overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Warehouse" className="h-full w-full object-cover opacity-20" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-8 py-16 md:py-24">
          <div className="inline-block bg-discount text-ink font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1 border border-ink mb-6">
            B-Stock · Auction Lots · For STR Operators
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[0.95]">
            LIQUIDATION STOCK FOR SHORT-TERM RENTAL OPERATORS
          </h1>
          <p className="font-mono text-sm md:text-base text-subtle max-w-xl mt-6 leading-relaxed">
            We buy auction lots so you don't have to. Small appliances, coffee makers, linens, electronics — every item priced below list with full condition transparency. Pick up local for free or ship UPS Ground.
          </p>
          <div className="flex flex-wrap gap-6 mt-8 font-mono text-xs uppercase tracking-wider">
            <span className="flex items-center gap-2"><Percent className="h-4 w-4 text-danger" /> Below List Pricing</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-success" /> Free Local Pickup In Fort Lauderdale</span>
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-link" /> UPS Ground Shipping</span>
          </div>
        </div>
      </section>

      <LiveActivityTicker />
      <BestSellersRow />

      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8" id="catalog">
        {/* FILTER BAR */}
        <div className="border-b-2 border-ink pb-4 mb-6">
          <div className="flex items-center gap-2 font-heading font-black text-lg tracking-tighter mb-4">
            <SlidersHorizontal className="h-4 w-4" /> FILTERS
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative lg:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-subtle" />
              <input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search SKU or name..."
                className="w-full border border-ink pl-9 pr-3 h-10 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-44">
                <FilterDropdown
                  label="Condition"
                  testid="filter-condition"
                  options={meta.conditions}
                  selected={conditions}
                  onToggle={(v) => toggle(conditions, setConditions, v)}
                  onClear={() => setConditions([])}
                  optionTestid={(c) => `filter-cond-${c}`}
                  optionLabel={(c) => CONDITION_SHORT[c] || c}
                  clearTestid="clear-conditions"
                />
              </div>
              {meta.categories.length > 0 && (
                <div className="w-44">
                  <FilterDropdown
                    label="Category"
                    testid="filter-category"
                    options={meta.categories}
                    selected={categories}
                    onToggle={(v) => toggle(categories, setCategories, v)}
                    onClear={() => setCategories([])}
                    optionTestid={(c) => `filter-cat-${c}`}
                    clearTestid="clear-categories"
                  />
                </div>
              )}
              <label data-testid="instock-filter" className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider cursor-pointer whitespace-nowrap border border-ink h-10 px-3 hover:bg-surface2 transition-colors">
                <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} className="h-4 w-4 accent-ink" />
                In stock only
              </label>
              <Link to="/lot-buys" data-testid="lot-buy-filter-btn" className="flex items-center gap-2 bg-link text-white h-10 px-4 font-mono text-xs uppercase tracking-wider border border-ink hover:bg-ink transition-colors whitespace-nowrap">
                <Layers className="h-4 w-4" /> Lot Buys
              </Link>
            </div>
          </div>
        </div>

        {/* GRID */}
        <div>
            <div className="flex items-center justify-between border-b-2 border-ink pb-2 mb-6">
              <div className="font-mono text-xs uppercase tracking-wider text-subtle" data-testid="results-count">
                {loading ? "Loading…" : `${skuGroups.length} products`}
              </div>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="border border-ink h-9 px-2 font-mono text-xs uppercase tracking-wider focus:outline-none"
              >
                <option value="newest">Newest</option>
                <option value="price_low">Price: Low → High</option>
                <option value="price_high">Price: High → Low</option>
                <option value="discount">Biggest Discount</option>
              </select>
            </div>

            {!loading && products.length === 0 ? (
              <div className="border border-dashed border-subtle p-12 text-center font-mono text-sm text-subtle">
                No products match your filters.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {skuGroups.map((g) => <ProductCard key={g.key} product={g.primary} variants={g.variants} />)}
              </div>
            )}
          </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t-2 border-ink bg-surface">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16">
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mb-10">HOW IT WORKS</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["01", "We Source The Lots", "We purchase surplus and returned inventory through wholesale auction lots — the same channel as B-Stock."],
              ["02", "Full Condition Transparency", "Every item is graded (New in Box, Like New, Scratch & Dent, Good/Fair, Used, As-Is…) with notes and our own photos so you know exactly what you're buying."],
              ["03", "Pickup Or Ship", "Grab it locally for $0 or we calculate live UPS Ground shipping plus Florida sales tax at checkout."],
            ].map(([n, t, d]) => (
              <div key={n} className="bg-paper border border-ink p-6">
                <div className="font-heading font-black text-4xl tracking-tighter text-discount" style={{ WebkitTextStroke: "1px #09090B" }}>{n}</div>
                <div className="font-heading font-bold text-lg mt-3">{t}</div>
                <p className="font-mono text-xs text-subtle mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function CheckOption({ checked, onChange, label, testid }) {
  return (
    <label
      data-testid={testid}
      className="flex items-center gap-2 px-2 py-1.5 font-mono text-xs cursor-pointer hover:bg-surface2 transition-colors"
    >
      <input type="checkbox" checked={checked} onChange={onChange} className="h-3.5 w-3.5 accent-ink shrink-0" />
      <span className="leading-tight">{label}</span>
    </label>
  );
}

function FilterDropdown({ label, testid, options, selected, onToggle, onClear, optionTestid, clearTestid, optionLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const count = selected.length;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 border border-ink h-10 px-3 font-mono text-xs uppercase tracking-wider transition-colors ${count > 0 ? "bg-ink text-paper" : "bg-paper hover:bg-surface2"}`}
      >
        <span>{label}{count > 0 ? ` · ${count}` : ""}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`${testid}-panel`} className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto border border-ink bg-paper shadow-hard">
          {count > 0 && (
            <button
              data-testid={clearTestid}
              onClick={onClear}
              className="w-full text-left px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-link hover:bg-surface2 border-b border-surface2"
            >
              Clear selection
            </button>
          )}
          {options.map((c) => (
            <CheckOption key={c} checked={selected.includes(c)} onChange={() => onToggle(c)} label={optionLabel ? optionLabel(c) : c} testid={optionTestid(c)} />
          ))}
        </div>
      )}
    </div>
  );
}
